import { z } from 'zod'

import {
	RoleContactSchema,
	createEmptyRoleContact,
} from '#app/routes/mocra+/_form+/components/contacts/schema.ts'
import {
	ACKNOWLEDGEMENT_CHANNEL_OPTIONS,
	ACKNOWLEDGEMENT_WINDOW_OPTIONS,
	CHANNEL_SYSTEM_FALLBACK_OPTIONS,
	COMPLAINT_CHANNEL_OPTIONS,
	COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS,
	COMPLAINT_SYSTEM_PLATFORM_OPTIONS,
	COMPLAINT_SYSTEM_USAGE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

const CHANNEL_SYSTEM_FALLBACK_VALUE_SET = new Set(
	CHANNEL_SYSTEM_FALLBACK_OPTIONS.map((option) => option.value.trim()),
)

const ComplaintChannelSchema = z.object({
	channel: z.enum(
		COMPLAINT_CHANNEL_OPTIONS.map((option) => option.value) as [
			(typeof COMPLAINT_CHANNEL_OPTIONS)[number]['value'],
			...string[],
		],
	),
	ownerType: z.enum(
		COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS.map((option) => option.value) as [
			(typeof COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS)[number]['value'],
			...string[],
		],
	),
	ownerName: z
		.string({
			required_error: 'Who operates or hosts this channel?',
		})
		.min(1, 'Who operates or hosts this channel?'),
	system: z
		.string({
			required_error: 'Select where this channel routes complaints.',
		})
		.min(1, 'Select where this channel routes complaints.'),
})

const ComplaintSystemSchema = z.object({
	platform: z.enum(
		COMPLAINT_SYSTEM_PLATFORM_OPTIONS.map((option) => option.value) as [
			(typeof COMPLAINT_SYSTEM_PLATFORM_OPTIONS)[number]['value'],
			...string[],
		],
	),
	name: z
		.string({
			required_error: 'Provide the system or tracker name.',
		})
		.min(1, 'Provide the system or tracker name.'),
	usage: z
		.array(
			z.enum(
				COMPLAINT_SYSTEM_USAGE_OPTIONS.map((option) => option.value) as [
					(typeof COMPLAINT_SYSTEM_USAGE_OPTIONS)[number]['value'],
					...string[],
				],
			),
			{
				required_error: 'Select at least one workflow usage.',
			},
		)
		.min(1, 'Select at least one workflow usage.'),
})

const AcknowledgementSchema = z
	.object({
		window: z.enum(
			ACKNOWLEDGEMENT_WINDOW_OPTIONS.map((option) => option.value) as [
				(typeof ACKNOWLEDGEMENT_WINDOW_OPTIONS)[number]['value'],
				...string[],
			],
		),
		channel: z.enum(
			ACKNOWLEDGEMENT_CHANNEL_OPTIONS.map((option) => option.value) as [
				(typeof ACKNOWLEDGEMENT_CHANNEL_OPTIONS)[number]['value'],
				...string[],
			],
		),
		coversWeekends: z.coerce.boolean().default(false),
		customWindow: z
			.string()
			.max(160, 'Keep the custom window description under 160 characters.')
			.optional(),
	})
	.superRefine((value, ctx) => {
		if (value.window === 'custom') {
			if (!value.customWindow || value.customWindow.trim().length === 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Describe the custom acknowledgement window.',
					path: ['customWindow'],
				})
			}
		} else if (value.customWindow && value.customWindow.trim().length > 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Remove the custom description when using a preset window.',
				path: ['customWindow'],
			})
		}
	})

const AdverseEventContactSchema = RoleContactSchema.extend({
	contactId: z.string().trim().optional(),
	fallbackName: z.string().optional(),
	fallbackEmail: z.string().email().optional(),
	fallbackTitle: z.string().optional(),
	fallbackPhone: z.string().optional(),
})

export const ADVERSE_EVENTS_SECTION_KEY = 'adverseEvents' as const

const AdverseEventContactArraySchema = z
	.array(AdverseEventContactSchema)
	.default([])

const MedicalReviewerSchema = AdverseEventContactSchema

const AdverseEventsSchema = z
	.object({
		intakeChannels: z
			.array(ComplaintChannelSchema, {
				required_error: 'Add at least one complaint intake channel.',
			})
			.min(1, 'Add at least one complaint intake channel.'),
		complaintSystems: z
			.array(ComplaintSystemSchema, {
				required_error: 'Add at least one complaint logging system.',
			})
			.min(1, 'Add at least one complaint logging system.'),
		acknowledgement: AcknowledgementSchema,
		medicalReviewers: z
			.array(MedicalReviewerSchema, {
				required_error: 'Add at least one medical reviewer.',
			})
			.min(1, 'Add at least one medical reviewer.'),
		seriousEventContacts: AdverseEventContactArraySchema,
		followUpContacts: AdverseEventContactArraySchema,
		trendAlertContacts: AdverseEventContactArraySchema,
	})
	.superRefine((value, ctx) => {
		const complaintSystems = Array.isArray(value.complaintSystems)
			? value.complaintSystems
			: []
		const intakeChannels = Array.isArray(value.intakeChannels)
			? value.intakeChannels
			: []
		const namedSystems = new Set(
			complaintSystems
				.map((system) => system.name.trim())
				.filter((name) => name.length > 0),
		)

		intakeChannels.forEach((channel, index) => {
			const target = channel.system.trim()
			if (
				CHANNEL_SYSTEM_FALLBACK_VALUE_SET.has(target) ||
				namedSystems.has(target)
			) {
				return
			}
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					'Pick an existing system of record above or choose a fallback option.',
				path: ['intakeChannels', index, 'system'],
			})
		})
	})

export type ComplaintChannelEntry = z.infer<typeof ComplaintChannelSchema>
export type ComplaintSystemEntry = z.infer<typeof ComplaintSystemSchema>
export type AcknowledgementEntry = z.infer<typeof AcknowledgementSchema>
export type MedicalReviewerEntry = z.infer<typeof MedicalReviewerSchema>
export type AdverseEventContactEntry = z.infer<typeof AdverseEventContactSchema>
export type AdverseEventsFormValues = z.infer<typeof AdverseEventsSchema>

function createComplaintChannel(
	channel: (typeof COMPLAINT_CHANNEL_OPTIONS)[number]['value'] = 'hotline',
): ComplaintChannelEntry {
	return {
		channel,
		ownerType: 'team',
		ownerName: '',
		system: '',
	}
}

function createComplaintSystem(): ComplaintSystemEntry {
	return {
		platform: 'zendesk',
		name: '',
		usage: ['intake'],
	}
}

function createAdverseEventContact(): AdverseEventContactEntry {
	return {
		...createEmptyRoleContact(),
		contactId: undefined,
		fallbackName: undefined,
		fallbackEmail: undefined,
		fallbackTitle: undefined,
		fallbackPhone: undefined,
	}
}

function createMedicalReviewer(): MedicalReviewerEntry {
	return {
		...createAdverseEventContact(),
		role: 'Medical reviewer',
		fallbackTitle: 'Medical reviewer',
	}
}

export const DEFAULT_VALUES: AdverseEventsFormValues = {
	intakeChannels: [],
	complaintSystems: [],
	acknowledgement: {
		window: 'within_1_business_day',
		channel: 'email',
		coversWeekends: false,
		customWindow: '',
	},
	medicalReviewers: [],
	seriousEventContacts: [],
	followUpContacts: [],
	trendAlertContacts: [],
}

export {
	AdverseEventsSchema,
	AcknowledgementSchema,
	ComplaintChannelSchema,
	ComplaintSystemSchema,
	AdverseEventContactSchema,
	AdverseEventContactArraySchema,
	MedicalReviewerSchema,
	createComplaintChannel,
	createComplaintSystem,
	createAdverseEventContact,
	createMedicalReviewer,
}
